import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
// Toolbar and format icons use the outlined set: even strokes that stay
// sharp at 19px, where the rounded set looks soft.
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeOutlined";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DownloadRoundedIcon from "@mui/icons-material/FileDownloadOutlined";
import FitScreenRoundedIcon from "@mui/icons-material/FitScreenOutlined";
import WidthFullOutlinedIcon from "@mui/icons-material/WidthFullOutlined";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenOutlined";
import HighlightOutlinedIcon from "@mui/icons-material/HighlightOutlined";
import HubRoundedIcon from "@mui/icons-material/HubOutlined";
import ImageNotSupportedRoundedIcon from "@mui/icons-material/ImageNotSupportedRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageOutlined";
import GridViewRoundedIcon from "@mui/icons-material/GridViewOutlined";
import LockRoundedIcon from "@mui/icons-material/LockOutlined";
import ManageSearchRoundedIcon from "@mui/icons-material/ManageSearchOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewOutlined";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import SchemaRoundedIcon from "@mui/icons-material/SchemaOutlined";
import TuneRoundedIcon from "@mui/icons-material/TuneOutlined";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutRoundedIcon from "@mui/icons-material/ZoomOutOutlined";
import {
  downloadDiagramFile,
  downloadGeneratedMap,
  getDiagram,
  getDiagramInfo,
  getProcessAttributesByPartialName,
  getProcessOccurences,
  getProcessRelationships,
  getProcessStructure,
} from "../../actions";
import { getShortName, sortByProperty } from "../../utils/accessMapFunctions";
import businessFlowsTheme, { brand } from "../../theme/businessFlowsTheme";
import { useViewConfig } from "../app/ViewConfigContext";
import { formatLabel, setting, typeLabel } from "../../utils/viewConfig";
import {
  PICTURE_FORMATS,
  SOURCE,
  STRUCTURE,
  availableFormats,
  chooseFormat,
  displaced,
  readFormat,
  sourceFormat,
  withFormat,
} from "./formatRoute";
import { ENHANCED, ORIGINAL, presentationFor, readPreference, writePreference } from "./svgPresentation";
import {
  CARDS,
  DIAGRAM,
  mapPresentationFor,
  readPreference as readMapPreference,
  writePreference as writeMapPreference,
} from "./mapPresentation";
import { describeFailure, withOneRetry } from "./loadState";
import { indexObjects, looksLikeObjectId, objectFor } from "./flowStyle";
import { DOWNLOAD_LOCKED, SHOW_MAPS } from "../../features";
import DetailsPanel from "../app/DetailsPanel";
import StructureCanvas from "./StructureCanvas";
import GeneratedCanvas from "./GeneratedCanvas";
import AskPanel from "./AskPanel";
import BpmnCanvas from "./BpmnCanvas";
import ImageCanvas from "./ImageCanvas";
import MermaidCanvas from "./MermaidCanvas";
import PdfCanvas from "./PdfCanvas";
import waterMark from "../../images/WaterMark.svg";
import "../auth/auth.css";

const FORMAT_ICONS = {
  [STRUCTURE]: GridViewRoundedIcon,
  bpmn: AccountTreeRoundedIcon,
  mermaid: SchemaRoundedIcon,
  pdf: PictureAsPdfRoundedIcon,
};

const viewIcon = (view) => {
  const Icon = FORMAT_ICONS[view] || ImageRoundedIcon;
  return <Icon sx={{ fontSize: 15 }} />;
};
const isStructureView = (view) => view === "structure";
const EMPTY_DETAILS = { loading: false, attributes: null, occurrences: [], relationships: [], properties: [], error: null };

// How long the modal details drawer waits, on a narrow screen, before it
// covers a step that could still be double-clicked. A shade longer than a
// browser's own double-click interval (500 ms is the usual system maximum;
// a reader who meant two separate clicks only waits this once).
const DOUBLE_CLICK_GRACE = 500;

// The quiet two-word switches in the toolbar (Diagram/Map,
// Enhanced/Original): one look, defined once.
const SMALL_SWITCH = {
  ml: 0.75,
  flexShrink: 0,
  alignSelf: "center",
  display: { xs: "none", sm: "inline-flex" },
  bgcolor: brand.canvas,
  border: `1px solid ${brand.line}`,
  borderRadius: 2.5,
  p: 0.3,
  gap: 0.3,
  "& .MuiToggleButton-root": {
    py: 0.2,
    px: 0.9,
    border: 0,
    borderRadius: "8px !important",
    fontSize: "0.68rem",
    fontWeight: 650,
    textTransform: "none",
    color: brand.muted,
    "&:hover": { bgcolor: "#fff", color: brand.ink },
  },
  "& .Mui-selected": { bgcolor: "#fff !important", color: `${brand.ink} !important`, boxShadow: `inset 0 0 0 1px ${brand.line}` },
};

// The formats this object's diagram info offers that the app actually draws:
// `downloadFormats` flags the rest (a DOCX, a ZIP, ...) - kept off the format
// tabs and the format-picking logic, and offered from the Files menu instead.
const drawableFormats = (data) => {
  const all = data?.formats || [];
  const downloadOnly = new Set(data?.downloadFormats || []);
  return all.filter((format) => !downloadOnly.has(format));
};

const formatBytes = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "";
  return value >= 1024 * 1024 ? `${(value / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(value / 1024))} KB`;
};

// Saves a blob under a name, the same way the toolbar's own download does.
const saveBlob = (blob, fileName) => {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
};

// A data URL or web URL for an image diagram.
const imageSource = (diagram) => {
  if (!diagram) return null;
  if (diagram.url) return diagram.url;
  const content = diagram.content || "";
  if (content.startsWith("data:")) return content;
  if (diagram.format === "svg") return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(content)}`;
  return `data:${diagram.mimeType};base64,${content}`;
};

// Toolbar buttons share one size and weight, and sit on white so the icons
// read clearly against the toolbar's own tint.
function ToolButton({ title, active, children, ...rest }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          aria-label={title}
          {...rest}
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            color: active ? brand.signal : brand.ink,
            bgcolor: active ? brand.ink : "#fff",
            border: `1px solid ${active ? brand.ink : brand.line}`,
            "&:hover": { bgcolor: active ? brand.inkSoft : brand.canvas, borderColor: brand.inkSoft },
            // A solid muted colour, not a faded one: a translucent icon reads
            // as blurred rather than unavailable.
            "&.Mui-disabled": { color: "#9aa0ab", bgcolor: "#fff", borderColor: brand.line, opacity: 1 },
            "& .MuiSvgIcon-root": { fontSize: 19 },
            ...rest.sx,
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

// A set of buttons that belong together, held in one rounded well.
function ToolGroup({ children }) {
  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{ p: 0.5, borderRadius: 3, bgcolor: brand.canvas, border: `1px solid ${brand.line}` }}
    >
      {children}
    </Stack>
  );
}

function CenteredMessage({ icon, title, text, children }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1.25} sx={{ height: "100%", p: 4, textAlign: "center" }}>
      {icon}
      <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
      {text && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380 }}>
          {text}
        </Typography>
      )}
      {children}
    </Stack>
  );
}

// A load that failed, said in words the reader can act on: what happened, a
// way to try again where trying again could help, whatever else the object
// offers, and the request's reference - the same one the app's error boundary
// shows, so a screenshot leads to the log lines.
function FailureCard({ failure, onRetry, children }) {
  const { title, text, canRetry, reference } = failure || {};
  return (
    <CenteredMessage
      icon={<ImageNotSupportedRoundedIcon sx={{ fontSize: 40, color: "text.disabled" }} />}
      title={title || "This couldn't be loaded"}
      text={text}
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap sx={{ rowGap: 1 }}>
        {canRetry !== false && onRetry && (
          <Button variant="contained" onClick={onRetry}>
            Try again
          </Button>
        )}
        {children}
      </Stack>
      {reference && (
        <Typography variant="caption" color="text.secondary">
          Reference {reference}
        </Typography>
      )}
    </CenteredMessage>
  );
}

export default function DiagramViewer(props) {
  const history = useHistory();
  const location = useLocation();
  const wide = useMediaQuery(businessFlowsTheme.breakpoints.up("lg"));
  // Room for the drawing, the question and the properties all three. Below
  // this, asking closes the properties panel rather than squeezing the
  // drawing into a column too narrow to read.
  const roomForBoth = useMediaQuery("(min-width:1600px)");
  const guid = decodeURIComponent(location.pathname.split("/")[3] || "");
  const user = props.user || {};
  const canvasRef = useRef(null);

  // Format names and which types open as a map come from the view configuration.
  const config = useViewConfig();
  const configRef = useRef(config);
  configRef.current = config;
  // The link's own format (?format=bpmn). Read when an object opens, so
  // rewriting the link afterwards never re-decides what is shown.
  const searchRef = useRef(location.search);
  searchRef.current = location.search;
  // Where the reader came from, carried in the history entry rather than the
  // link: it belongs to this step, not to the object, so a copied link never
  // hides a part from whoever it is sent to.
  const stateRef = useRef(location.state);
  stateRef.current = location.state;

  // An object opened by its fallback id may be known by a real GUID (or have
  // just been given one): the link replaces itself with the canonical one, so
  // what a reader copies names the object rather than a node number. Replaced,
  // never pushed - Back leaves the diagram - and the query string, so
  // ?format= survives. A ref, so the load effect needs no extra dependency.
  const replaceCanonical = useRef(null);
  replaceCanonical.current = (canonical) => {
    if (typeof canonical !== "string" || !canonical || canonical === guid) return;
    history.replace({
      pathname: `${props.path}/map/${encodeURIComponent(canonical)}`,
      search: searchRef.current,
      state: stateRef.current,
    });
  };

  const [info, setInfo] = useState({ loading: true, error: "", data: null });
  const [format, setFormat] = useState(null);
  // Whether the reader has picked a format themselves: once they have, the
  // view configuration arriving late (or refreshing) never overrides it.
  const userPickedFormat = useRef(false);
  // `format` on this state says which format the payload belongs to: switching
  // format must never hand the previous file to the new canvas.
  const [diagram, setDiagram] = useState({ loading: false, error: "", data: null, format: null });
  const [structure, setStructure] = useState([]);
  // The objects around this one, whether or not maps are shown: the Flow
  // colours an element in its object's type colour where it can name the
  // object, and these - with the object itself - are the objects it knows.
  const [related, setRelated] = useState([]);
  // What the map says about itself beside its parts: one entry per
  // relationship (label, direction, count), and whether there were more parts
  // than the server returns in one answer.
  const [structureMap, setStructureMap] = useState({ groups: [], total: 0, truncated: false, flowOrdered: false });
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(EMPTY_DETAILS);
  // What the drawing says about itself, and whether the reader is looking at
  // the object the drawing belongs to (which is what the empty canvas selects).
  const diagramProps = useRef([]);
  const diagramObjectSelected = useRef(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Ask this diagram: what the drawing contains, and whether the panel that
  // asks it questions is open. The elements are read off the canvas once, on
  // import - nothing is fetched for it and nothing is sent anywhere.
  const [elements, setElements] = useState([]);
  const [askOpen, setAskOpen] = useState(false);
  const [askDrawerOpen, setAskDrawerOpen] = useState(false);
  // What the pointer is over on the canvas, so the list beside it can point
  // at the same thing.
  const [pointedAt, setPointedAt] = useState(null);
  const askButtonRef = useRef(null);
  // The pending "open the details drawer" from a first click - see
  // onSelectElement.
  const drawerTimer = useRef(0);
  const [drillableCount, setDrillableCount] = useState(0);
  const [filesMenuAnchor, setFilesMenuAnchor] = useState(null);
  // Bumped by "Try again": both loads watch it, so one button re-runs
  // whichever of them failed without the reader reloading the page.
  const [attempt, setAttempt] = useState(0);
  const tryAgain = useCallback(() => setAttempt((n) => n + 1), []);
  // Enhanced or Original, for a stored picture. The reader's own choice, kept
  // in this browser; until they make one the database's view setting decides.
  const [presentationChoice, setPresentationChoice] = useState(readPreference);
  const presentation = presentationFor(presentationChoice, setting(config, "diagram.enhanceSvg"));
  // How the map is shown: the diagram the app draws from the object's own
  // parts, or the cards that say more about each one. The reader's choice,
  // over the database's default.
  const [mapChoice, setMapChoice] = useState(readMapPreference);
  const mapPresentation = mapPresentationFor(mapChoice, setting(config, "diagram.generatedMap"));
  // Whether this link asked for the imported picture by name (?format=source).
  // It is not one of the views where we draw the object, but a reader who
  // followed such a link is looking at it, so it keeps its place in the
  // toolbar for as long as they are on this object - never a picture on screen
  // with no tab to say so.
  const [sourceByLink, setSourceByLink] = useState(false);
  // Whether the imported picture is offered as a view: the database's own
  // choice (`diagram.showSourcePicture`, off by default), or this reader's link.
  const showSource = setting(config, "diagram.showSourcePicture") === true || sourceByLink;


  // Every way this object can be shown: its structure map plus any attached
  // diagram formats - a file the app doesn't draw is never one of the tabs.
  const formats = useMemo(
    () =>
      availableFormats({
        drawn: drawableFormats(info.data),
        hasStructure: structure.length > 0,
        // Stored exports of container objects are static and often unreadable; the live map replaces them.
        mapFirstType: setting(config, "diagram.mapFirstTypes").includes(info.data?.type),
        // ...and the rule behind that: an object with nothing the app can
        // draw itself opens on its live map, whatever its type is called.
        mapBeforeExports: setting(config, "diagram.mapBeforeExports") !== false,
        // ...and the rule the product owner asked for: where the only picture
        // an object has is somebody's export, the picture the product offers
        // is the one it draws itself, and the export moves behind its own
        // name. Nothing here knows a type; `displaced` is the whole test.
        mapAsSvg: setting(config, "diagram.mapAsSvg") !== false,
        // ...and the one he asked for after seeing it: where we draw the
        // object, the imported export isn't offered as a view at all.
        showSource,
      }),
    [info.data, structure, config, showSource]
  );

  // Which stored format the "Source picture" choice actually reads. The tab is
  // a name for the file exactly as it was imported; the format is whichever
  // picture the object stores.
  const storedPicture = useMemo(() => sourceFormat({ drawn: drawableFormats(info.data) }), [info.data]);
  // Whether our own drawing is this object's picture. Decides the Download
  // button's answer, and nothing else here.
  const ourDrawing = useMemo(
    () =>
      displaced({
        drawn: drawableFormats(info.data),
        hasStructure: structure.length > 0,
        mapAsSvg: setting(config, "diagram.mapAsSvg") !== false,
      }),
    [info.data, structure, config]
  );

  // Attached files the app doesn't draw: name and size, for the Files menu.
  // Where we draw the object ourselves, the picture it was imported with is
  // not one of the views - so it is offered here instead, under the name it
  // was stored with. The file is never taken away, only taken off the toolbar.
  const files = useMemo(() => {
    const attached = info.data?.files || [];
    const shownAsAView = new Set(formats);
    if (shownAsAView.has(SOURCE)) return attached;
    const stored = info.data?.storedFiles || [];
    const hidden = drawableFormats(info.data)
      .filter((format) => PICTURE_FORMATS.includes(format) && !shownAsAView.has(format))
      .map((format) => stored.find((file) => file.format === format) || { format, fileName: null, bytes: null });
    return hidden.length ? [...attached, ...hidden] : attached;
  }, [info.data, formats]);

  // The objects a diagram element can be recognised as: this object and the
  // parts around it. One array identity per load, so the Flow's decoration
  // doesn't run again on every render.
  const knownObjects = useMemo(
    () =>
      info.data
        ? [{ GUID: info.data.guid, name: info.data.name, Type: info.data.type }, ...related]
        : related,
    [info.data, related]
  );

  // The object and the formats it has.
  useEffect(() => {
    let active = true;
    setInfo({ loading: true, error: "", data: null });
    setDiagram({ loading: false, error: "", data: null, format: null });
    setFormat(null);
    setStructure([]);
    setRelated([]);
    setStructureMap({ groups: [], total: 0, truncated: false, flowOrdered: false });
    // A new object to look at: any earlier choice no longer applies.
    userPickedFormat.current = false;
    // ...including a link that named the imported picture.
    setSourceByLink(readFormat(searchRef.current) === SOURCE);
    // The object this one was opened from: its map never offers it as a part.
    const from = typeof stateRef.current?.from === "string" && stateRef.current.from !== guid ? stateRef.current.from : null;
    // Asked once more, quietly, where the first answer reads like bad timing -
    // a server restarting mid-request used to show "try again in a moment"
    // for something that would have worked on the next attempt.
    const stillWanted = () => active;
    Promise.all([
      withOneRetry(() => getDiagramInfo(guid), { stillWanted }),
      withOneRetry(() => getProcessStructure(guid, from), { stillWanted }),
    ]).then(([res, structureRes]) => {
      if (!active) return;
      if (!res?.success) {
        // What went wrong, in words, with the request's own reference: a
        // database that can't be reached, a refusal, a server we couldn't
        // get to and "no such object" are four different things.
        setInfo({ loading: false, error: "failed", failure: describeFailure(res, { subject: "object" }), data: null });
        return;
      }
      const data = res.content;
      // SHOW_MAPS=false: structure maps are hidden as a user-facing thing, so
      // a mapFirstType with no stored diagram falls to "No diagram for this
      // object yet" / Explore in the Relationship Explorer, below, rather
      // than opening its live map. Flip the switch in ../../features to
      // bring maps back.
      const assignments = Array.isArray(structureRes?.content?.assignments) ? structureRes.content.assignments : [];
      const parts = SHOW_MAPS ? assignments : [];
      // The link may name the format; a format this object doesn't have (or a
      // word that isn't one) simply opens on what it would have opened on.
      //
      // Decided - and written down - BEFORE a single piece of state is set.
      // React 17 doesn't batch updates made from a promise, so every setState
      // below renders and runs the effects on its own; with the decision
      // still unrecorded, the effect that re-applies the view configuration
      // saw a reader who had chosen nothing and opened the map over the
      // format the link had asked for.
      const { format: chosen, fromLink } = chooseFormat({
        requested: readFormat(searchRef.current),
        drawn: drawableFormats(data),
        hasStructure: parts.length > 0,
        mapFirstType: setting(configRef.current, "diagram.mapFirstTypes").includes(data.type),
        mapBeforeExports: setting(configRef.current, "diagram.mapBeforeExports") !== false,
        mapAsSvg: setting(configRef.current, "diagram.mapAsSvg") !== false,
        // The database's own answer only: a link that names the imported file
        // is honoured by chooseFormat itself, offered as a view or not.
        showSource: setting(configRef.current, "diagram.showSourcePicture") === true,
      });
      userPickedFormat.current = fromLink;
      setStructure(parts);
      // Kept whether or not maps are shown: this is what the Flow reads to
      // colour an element in its object's own type colour.
      setRelated(assignments);
      setStructureMap({
        groups: Array.isArray(structureRes?.content?.groups) ? structureRes.content.groups : [],
        total: Number(structureRes?.content?.total) || parts.length,
        truncated: structureRes?.content?.truncated === true,
        flowOrdered: structureRes?.content?.flowOrdered === true,
      });
      setInfo({ loading: false, error: "", data });
      // The object may be known by an id other than the one asked for, and
      // the link follows it. The header first: X-Object-Id is only ever set
      // where the object has just been given a real id, which is the one to
      // keep; otherwise the id the server answered under.
      replaceCanonical.current?.(res.objectId || data?.guid);
      setFormat(chosen);
    });
    return () => {
      active = false;
    };
  }, [guid, attempt]);

  // The view configuration can still be loading (or on its fallback list)
  // when the effect above first decides the format. Once it flips to loaded -
  // possibly with a customised map-first list - re-apply that decision, but
  // only while the reader hasn't chosen a format themselves.
  useEffect(() => {
    if (!config.loaded || userPickedFormat.current) return;
    if (!info.data || !structure.length) return;
    if (formats[0] === STRUCTURE) setFormat(STRUCTURE);
  }, [config, info.data, structure, formats]);

  // Whatever set `format`, it must be one of the formats actually on offer -
  // otherwise the reader is stuck with no diagram and no map tab selected.
  useEffect(() => {
    if (format && formats.length && !formats.includes(format)) setFormat(formats[0]);
  }, [format, formats]);

  // The link follows what is shown, so it can be copied and shared as it is.
  // Replaced, never pushed: Back leaves the diagram instead of stepping
  // through the formats. Only once this object's own format is settled - a
  // format still belonging to the object just left would name the wrong one.
  useEffect(() => {
    if (!format || info.data?.guid !== guid) return;
    // A reader who followed "?format=source" is looking at the imported file,
    // and the link keeps saying so: rewriting it to the stored format would
    // name a view this object doesn't offer, and the next reader of that link
    // would land somewhere else.
    const named = sourceByLink && (format === SOURCE || format === storedPicture) ? SOURCE : format;
    // The history entry's own state (where the reader came from) is carried
    // over: it belongs to this step, and rewriting the link must not lose it.
    const search = withFormat(location.search, named);
    if (search) history.replace({ pathname: location.pathname, search, state: location.state });
  }, [format, storedPicture, sourceByLink, guid, info.data, location.pathname, location.search, location.state, history]);

  // The diagram in the chosen format. "Source picture" is a name for a tab,
  // not a stored format: what it reads is the picture the object actually has.
  const wanted = format === SOURCE ? storedPicture : format;
  useEffect(() => {
    if (!format || format === STRUCTURE || !wanted) {
      setDiagram({ loading: false, error: "", data: null, format });
      return undefined;
    }
    let active = true;
    setDiagram({ loading: true, error: "", data: null, format });
    // One retry before saying anything: a diagram that arrives on the second
    // attempt should never flash an error at the reader.
    withOneRetry(() => getDiagram(guid, wanted, { presentation }), { stillWanted: () => active }).then((res) => {
      if (!active) return;
      setDiagram(
        res?.success
          ? { loading: false, error: "", data: res.content, format }
          : {
              loading: false,
              error: "failed",
              failure: describeFailure(res, { subject: formatLabel(configRef.current, format) }),
              data: null,
              format,
            }
      );
    });
    return () => {
      active = false;
    };
    // `presentation` is one of the two words, never a free value, so a reader
    // switching it re-reads the same diagram and nothing else.
  }, [guid, format, wanted, attempt, presentation]);

  const loadDetails = useCallback(async (target) => {
    setSelected(target);
    diagramObjectSelected.current = Boolean(target.isDiagramObject);
    // What the file itself says about the element the reader clicked. It is
    // there before anything is asked of anybody, and it stays there whatever
    // the library says. For the diagram's own object it is read afresh each
    // time: the canvas finishes reading the file while these answers are in
    // flight, and whichever lands last must not throw the other away.
    const fromDiagram = () => (target.isDiagramObject ? diagramProps.current : target.properties || []);
    // An element that stands for no object in the library is not asked about:
    // the answer could only be "no such object", and a refusal is not a set of
    // attributes. This is the ordinary case for an imported model.
    if (!target.GUID) {
      setDetails({ ...EMPTY_DETAILS, properties: fromDiagram() });
      return;
    }
    setDetails({ ...EMPTY_DETAILS, loading: true, properties: fromDiagram() });
    // Diagram element ids aren't always object GUIDs: fall back to the element's name.
    let attributes = await getProcessAttributesByPartialName(target.GUID);
    if (!attributes?.content && target.name) attributes = await getProcessAttributesByPartialName(target.name);
    // A refusal is the server's own words, never rows in the panel: reading
    // one as attributes is what showed a reader "Status 404" where the object's
    // properties belong.
    const refused = !attributes || attributes.success === false;
    const objectGuid = attributes?.content?.GUID || target.GUID;
    const [occurrences, relationships] = refused
      ? [[], []]
      : await Promise.all([getProcessOccurences(objectGuid), getProcessRelationships(objectGuid)]);
    setSelected((current) => {
      if (current?.GUID === target.GUID) {
        setDetails({
          loading: false,
          attributes: refused ? null : attributes,
          // Where the file already says something, a library that has nothing
          // to add is not a failure worth a sentence.
          error: refused && !fromDiagram().length ? attributes?.msg || null : null,
          properties: fromDiagram(),
          occurrences: Array.isArray(occurrences) ? [...occurrences].sort(sortByProperty("name")) : [],
          relationships: Array.isArray(relationships) ? [...relationships].sort(sortByProperty("name")) : [],
        });
      }
      return current;
    });
  }, []);

  // Start with the diagram's own object selected, with whatever the file says
  // about the drawing as a whole beside its stored attributes.
  useEffect(() => {
    if (info.data)
      loadDetails({
        GUID: info.data.guid,
        name: info.data.name,
        Type: info.data.type,
        isDiagramObject: true,
        properties: diagramProps.current,
      });
  }, [info.data, loadDetails]);

  // The file's own account of the drawing arrives when the canvas has read it,
  // which is after the object's details were asked for. Nothing is asked for
  // again: the rows are simply added to what is already on screen.
  const onDiagramProperties = useCallback((rows) => {
    diagramProps.current = rows || [];
    if (!diagramObjectSelected.current) return;
    setDetails((current) => ({ ...current, properties: diagramProps.current }));
  }, []);

  const onSelectElement = useCallback(
    (element) => {
      if (!element) {
        if (info.data)
          loadDetails({
            GUID: info.data.guid,
            name: info.data.name,
            Type: info.data.type,
            isDiagramObject: true,
            properties: diagramProps.current,
          });
        return;
      }
      // The same objects the Flow colours an element by also say what it is:
      // an element we can name is shown under its own type.
      const known = objectFor(indexObjects(knownObjects), { guid: element.guid, name: element.name });
      // Whether this element stands for an object at all: one the viewer
      // already holds, or one whose id is an id the library could answer for.
      // Everything else is a shape in somebody's file, and the file is what
      // says what it is.
      const inLibrary = Boolean(known) || looksLikeObjectId(element.guid);
      loadDetails({
        GUID: inLibrary ? known?.guid || element.guid : "",
        name: element.name,
        Type: known?.objectType,
        kind: element.kind,
        properties: element.properties,
      });
      // The details drawer on a narrow screen is a modal: the moment it opens,
      // its backdrop covers the canvas. Opened on the first click of a
      // double-click, it swallows the second one and the reader can never
      // drill into a step - which is exactly what "I cannot double click"
      // was. So for a step that has a diagram of its own the drawer waits out
      // the double-click interval, and drilling in cancels it. A step with
      // nothing to drill into opens it at once, as before.
      if (wide) return;
      window.clearTimeout(drawerTimer.current);
      if (!element.hasDiagram) {
        setDrawerOpen(true);
        return;
      }
      drawerTimer.current = window.setTimeout(() => setDrawerOpen(true), DOUBLE_CLICK_GRACE);
    },
    [info.data, knownObjects, loadDetails, wide]
  );

  // Nothing opens after the viewer has gone.
  useEffect(() => () => window.clearTimeout(drawerTimer.current), []);

  // Pushed, so Back climbs out one level at a time - map by map, to any depth.
  // A format (the map's cards pass "structure" for a part with no diagram of
  // its own) goes in the link, as any deep link carries it.
  // The step also remembers which object it was taken from, in the history
  // entry rather than the link: the map that opens leaves that object out, so
  // the way back is never offered as a way on.
  const openDiagram = useCallback(
    (target, format) => {
      // The reader went one level in: the details drawer the first click
      // asked for is no longer what they wanted.
      window.clearTimeout(drawerTimer.current);
      setDrawerOpen(false);
      return history.push({
        pathname: `${props.path}/map/${encodeURIComponent(target)}`,
        search: format ? `?format=${encodeURIComponent(format)}` : "",
        state: { from: info.data?.guid || guid },
      });
    },
    [history, props.path, info.data, guid]
  );

  const openInExplorer = (target = info.data) => {
    if (!target) return;
    props.setNodeDatum?.({
      id: target.guid,
      guid: target.guid,
      shortName: getShortName(target.name || ""),
      fullName: target.name,
      type: target.type,
      description: target.description,
      children: [],
      isLink: false,
      style: { background: "#FFFFFF" },
    });
    history.push(`${props.path}/object/${target.guid}`);
  };

  const entitlements = user.entitlements;
  // Where our own drawing is the object's picture, Download hands over that
  // drawing - the same picture on screen, written by the server from the same
  // layout - named after the object. The stored file is still downloaded from
  // its own tab. The card list is a list, and has nothing to hand over.
  const drawingIsDownload = format === STRUCTURE && ourDrawing && mapPresentation === DIAGRAM;
  const mayExport = (key) => (entitlements ? Boolean(entitlements[key]) : user.subscription === "ultimatePlus");
  const canDownload =
    isStructureView(format) ? drawingIsDownload && mayExport("export.svg") : mayExport(format === "bpmn" ? "export.bpmn" : "export.svg");

  const download = async () => {
    if (drawingIsDownload) {
      const res = await downloadGeneratedMap(guid);
      // Named after the object, not after the format: this file is the object
      // drawn, and nobody sent it to us.
      if (res?.success && res.blob) saveBlob(res.blob, `${info.data?.name || "diagram"}.svg`);
      return;
    }
    const d = diagram.data;
    if (!d) return;
    const name = `${d.name || info.data?.name || "diagram"}.${d.format}`;
    if (d.url) {
      window.open(d.url, "_blank", "noopener");
      return;
    }
    let blob;
    // Bytes (an image, a PDF) are decoded; text (BPMN, SVG, a Mermaid source)
    // is written out as it is.
    const binary = d.isBinary === undefined ? d.isImage && d.format !== "svg" : d.isBinary;
    if (binary) {
      const base64 = (d.content || "").replace(/^data:[^,]+,/, "");
      const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      blob = new Blob([bytes], { type: d.mimeType });
    } else {
      blob = new Blob([d.content || ""], { type: d.mimeType });
    }
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  };

  // Any attached file downloads the same way, whatever format it stopped at:
  // the current version's bytes, saved under its own stored name.
  const downloadFile = async (file) => {
    setFilesMenuAnchor(null);
    const res = await downloadDiagramFile(undefined, guid, file.format);
    if (res?.success && res.blob) {
      saveBlob(res.blob, res.fileName || file.fileName || `${info.data?.name || "file"}.${file.format}`);
    }
  };

  const fullscreen = () => {
    const el = document.getElementById("bf-diagram-stage");
    (el?.requestFullscreen || el?.webkitRequestFullscreen)?.call(el);
  };

  const isStructure = format === STRUCTURE;
  // A payload from the format we just left is not this format's diagram.
  const shown = diagram.format === format ? diagram : { loading: true, error: "", data: null, format };
  // What the thing in front of the reader actually IS, which is never the name
  // on its tab: "Source picture" is a name for a choice, and the file behind it
  // is an SVG, a PNG or whatever the object stores. Every decision below - which
  // canvas draws it, whether it can be zoomed, what Download hands over - is
  // made on this, so renaming a choice can never make a picture undrawable.
  const shownFormat = shown.data?.format || format;
  // A PDF is read in the browser's own viewer, which brings its own zoom.
  // The drawn map has its own zoom, pan and fit; the card list and a PDF do
  // not (a PDF is read in the browser's own viewer, which brings its own).
  const zoomDisabled = isStructure ? mapPresentation !== DIAGRAM : !shown.data || shownFormat === "pdf";
  // A picture the product can restyle has two ways of being shown; a flow we
  // draw ourselves, a photograph and a document each have one, so the switch
  // is offered only where there is something to switch between. The server
  // says so itself (`canEnhance`), and what came back says which one it is.
  const canSwitchPresentation = Boolean(shown.data && shown.data.canEnhance);
  // Whether this object has a drawing of our own to offer beside the stored
  // file: parts in the graph is the whole test - no type is named anywhere.
  const hasMap = structure.length > 0;
  const showingEnhanced = shown.data?.presentation === ENHANCED;
  const showPanel = wide && panelOpen;
  // The panel only makes sense where the drawing is made of elements the
  // reader can be shown one by one - which is the canvas that reads a model,
  // not a picture of one. That is the whole test; no format is named twice.
  const canAsk = shownFormat === "bpmn" && elements.length > 0;
  // A different format is a different drawing: what the last one contained is
  // not something this one can be asked about.
  useEffect(() => {
    if (shownFormat === "bpmn") return;
    setElements([]);
    setAskOpen(false);
    setAskDrawerOpen(false);
  }, [shownFormat]);
  const showAsk = canAsk && askOpen && wide;
  const askIsOpen = askOpen || askDrawerOpen;
  // Ids in, classes on the canvas out. Stable, so the panel isn't re-running
  // its answer because the viewer re-rendered.
  const highlight = useCallback((ids) => canvasRef.current?.highlight?.(ids), []);
  const frame = useCallback((ids, options) => canvasRef.current?.frame?.(ids, options), []);
  const reveal = useCallback((id, options) => canvasRef.current?.reveal?.(id, options), []);
  const preview = useCallback((id) => canvasRef.current?.preview?.(id), []);
  const closeAsk = useCallback(() => {
    canvasRef.current?.highlight?.([]);
    canvasRef.current?.preview?.(null);
    setAskOpen(false);
    setAskDrawerOpen(false);
    // Back where it was opened from, so the keyboard doesn't land nowhere.
    askButtonRef.current?.focus?.();
  }, []);
  const openAsk = useCallback(() => {
    if (wide) {
      setAskOpen(true);
      // Two panels and a drawing need the width for all three.
      if (!roomForBoth) setPanelOpen(false);
      return;
    }
    setAskDrawerOpen(true);
  }, [wide, roomForBoth]);

  // One key opens it and closes it again - never while somebody is typing
  // somewhere else on the page.
  useEffect(() => {
    if (!canAsk) return undefined;
    const onKey = (event) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const on = event.target;
      if (on && (on.tagName === "INPUT" || on.tagName === "TEXTAREA" || on.isContentEditable)) return;
      event.preventDefault();
      if (askIsOpen) closeAsk();
      else openAsk();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canAsk, askIsOpen, closeAsk, openAsk]);
  // The plan decides this: "feature.watermark" is part of a plan's
  // entitlements, and trial plans carry it.
  const isTrial = user.entitlements?.["feature.watermark"] ?? user.subscription === "trial";

  // Advanced search opened on the selected element (or this diagram's object).
  const openInSearch = () => {
    const target = selected?.GUID
      ? { guid: selected.GUID, name: selected.name, type: selected.Type }
      : info.data
      ? { guid: info.data.guid, name: info.data.name, type: info.data.type }
      : null;
    if (!target) {
      history.push(`${props.path}/advance-search`);
      return;
    }
    const params = new URLSearchParams({ q: target.name || "", guid: target.guid });
    if (target.type) params.set("type", target.type);
    history.push(`${props.path}/advance-search?${params.toString()}`);
  };

  const openRelationship = (rel) => openInExplorer({ guid: rel.GUID, name: rel.name, type: rel.Type });

  const detailsPanel = (
    <DetailsPanel
      selected={selected}
      heading={selected?.Type ? typeLabel(config, selected.Type) : selected?.kind || "Properties"}
      details={details}
      path={props.path}
      emptyText="Click an element in the diagram to see its attributes, occurrences and relationships."
      onClose={wide ? () => setPanelOpen(false) : () => setDrawerOpen(false)}
      onOpenOccurrence={(occ) => openDiagram(occ.GUID)}
      onOpenRelationship={openRelationship}
      onFindInSearch={openInSearch}
      // Only where there is an object to open: an element of an imported model
      // is a shape in a file, and the explorer has nothing to show for it.
      onOpenExplorer={
        selected?.GUID ? (item) => openInExplorer({ guid: item.GUID, name: item.name, type: item.Type }) : undefined
      }
    />
  );

  const askPanel = (
    <AskPanel
      elements={elements}
      objects={knownObjects}
      title={info.data?.name}
      pointedAt={pointedAt}
      onHighlight={highlight}
      onFrame={frame}
      onReveal={reveal}
      onPreview={preview}
      onClose={closeAsk}
    />
  );

  const onSelectPart = (item) => {
    loadDetails({ GUID: item.GUID, name: item.name, Type: item.Type });
    if (!wide) setDrawerOpen(true);
  };

  let stage;
  if (isStructure && !info.loading && mapPresentation === DIAGRAM) {
    // The object drawn from what the library actually holds: its parts, the
    // relationships they were reached by, their order and this database's own
    // colours. Nothing about it is stored anywhere - it is derived every time.
    stage = (
      <GeneratedCanvas
        ref={canvasRef}
        object={info.data ? { GUID: info.data.guid, name: info.data.name, Type: info.data.type } : null}
        items={structure}
        total={structureMap.total}
        truncated={structureMap.truncated}
        flowOrdered={structureMap.flowOrdered}
        subscription={user.subscription}
        selectedGuid={selected?.GUID}
        onSelect={(item) => item?.GUID && onSelectPart(item)}
        onOpen={openDiagram}
      />
    );
  } else if (isStructure && !info.loading) {
    stage = (
      <StructureCanvas
        items={structure}
        groups={structureMap.groups}
        total={structureMap.total}
        truncated={structureMap.truncated}
        // Where the order came from the object's own flow, the map says so:
        // a customer reading the two side by side should be told why they
        // agree rather than left to notice it.
        flowOrdered={structureMap.flowOrdered}
        // A part's own contents are fetched for the preview on its card: this
        // object is where that fetch comes from, so it isn't listed inside its
        // own part.
        fromGuid={info.data?.guid || guid}
        subscription={user.subscription}
        selectedGuid={selected?.GUID}
        onSelect={onSelectPart}
        onOpen={openDiagram}
      />
    );
  } else if (info.loading || shown.loading) {
    stage = (
      <CenteredMessage icon={<CircularProgress size={28} />} title="Loading diagram…" />
    );
  } else if (info.error) {
    // The object itself didn't load: nothing is known about its formats, so
    // the way on is a search, or back.
    stage = (
      <FailureCard failure={info.failure} onRetry={tryAgain}>
        <Button variant="outlined" startIcon={<ManageSearchRoundedIcon />} onClick={() => history.push(`${props.path}/advance-search`)}>
          Search
        </Button>
        <Button onClick={() => history.goBack()}>Go back</Button>
      </FailureCard>
    );
  } else if (shown.error) {
    // One format didn't load. The object did, so everything it holds is
    // still on offer - never a dead end. What those formats are, and what
    // they are called, comes from the server and the view configuration.
    stage = (
      <FailureCard
        failure={
          shown.failure || {
            title: `${formatLabel(config, format)} couldn't be drawn`,
            text: shown.error,
            canRetry: true,
          }
        }
        onRetry={tryAgain}
      >
        {formats
          .filter((other) => other !== format)
          .map((other) => (
            <Button
              key={other}
              variant="outlined"
              startIcon={viewIcon(other)}
              onClick={() => {
                userPickedFormat.current = true;
                setFormat(other);
              }}
            >
              {isStructureView(other) ? `Open its ${formatLabel(config, other).toLowerCase()}` : `Open ${formatLabel(config, other)}`}
            </Button>
          ))}
        <Button variant="outlined" startIcon={<HubRoundedIcon />} onClick={() => openInExplorer()}>
          Open in Relationship Explorer
        </Button>
      </FailureCard>
    );
  } else if (shownFormat === "bpmn" && shown.data?.content) {
    stage = (
      <BpmnCanvas
        ref={canvasRef}
        xml={shown.data.content}
        objects={knownObjects}
        onSelect={onSelectElement}
        onDiagramProperties={onDiagramProperties}
        onElements={setElements}
        onHoverElement={setPointedAt}
        onDrill={openDiagram}
        onError={(msg) => setDiagram({ loading: false, error: msg, data: null, format })}
        onDrillableCount={setDrillableCount}
      />
    );
  } else if (shownFormat === "mermaid" && shown.data?.content) {
    stage = (
      <MermaidCanvas
        ref={canvasRef}
        source={shown.data.content}
        // The canvas shows the parser's complaint itself; this only keeps the
        // reader's choice of format out of the toolbar's "nothing to show".
        onError={() => {}}
      />
    );
  } else if (shownFormat === "pdf" && (shown.data?.content || shown.data?.url)) {
    stage = (
      <PdfCanvas
        ref={canvasRef}
        url={shown.data.url}
        content={shown.data.content}
        name={info.data?.name}
        onDownload={canDownload ? download : null}
      />
    );
  } else if (shown.data && (shown.data.isImage || shownFormat === "svg")) {
    const src = imageSource(shown.data);
    stage = src ? (
      // A vector is scaled by re-drawing it, not by stretching a bitmap of
      // it: `markup` lets the canvas read the drawing's own size (its width
      // and height, else its viewBox) before the browser has guessed one.
      <ImageCanvas
        ref={canvasRef}
        src={src}
        alt={info.data?.name || "Diagram"}
        vector={shownFormat === "svg"}
        markup={shownFormat === "svg" ? shown.data.content : undefined}
      />
    ) : (
      <Alert severity="warning" sx={{ m: 2 }}>This diagram has no content.</Alert>
    );
  } else if (shown.data && !shown.data.content && !shown.data.url) {
    // The diagram is there but holds nothing this format can be drawn from -
    // an import that saved the node and not the document. Say that plainly:
    // "download it to open it in a suitable tool" would send the reader after
    // a file that doesn't exist. Offer what the object does have instead.
    stage = (
      <CenteredMessage
        icon={<ImageNotSupportedRoundedIcon sx={{ fontSize: 40, color: "text.disabled" }} />}
        title={`This ${formatLabel(config, format).toLowerCase()} has no content stored`}
        text="Nothing was saved with it, so there is nothing to draw."
      >
        <Stack direction="row" spacing={1}>
          {structure.length > 0 && (
            <Button
              variant="contained"
              startIcon={<GridViewRoundedIcon />}
              onClick={() => {
                userPickedFormat.current = true;
                setFormat(STRUCTURE);
              }}
            >
              Open its map
            </Button>
          )}
          <Button
            variant={structure.length > 0 ? "outlined" : "contained"}
            startIcon={<HubRoundedIcon />}
            onClick={() => openInExplorer()}
          >
            Open in Relationship Explorer
          </Button>
        </Stack>
      </CenteredMessage>
    );
  } else if (shown.data) {
    stage = (
      <CenteredMessage
        icon={<ImageNotSupportedRoundedIcon sx={{ fontSize: 40, color: "text.disabled" }} />}
        title={`${formatLabel(config, format)} can't be shown in the browser yet`}
        text={shown.data.url ? "It is kept elsewhere; open it where it lives." : canDownload ? "Download it to open it in a suitable tool." : undefined}
      >
        {shown.data.url && (
          <Button variant="contained" startIcon={<OpenInNewRoundedIcon />} href={shown.data.url} target="_blank" rel="noopener noreferrer">
            Open the file
          </Button>
        )}
      </CenteredMessage>
    );
  } else {
    stage = (
      <CenteredMessage
        icon={<AccountTreeRoundedIcon sx={{ fontSize: 40, color: "text.disabled" }} />}
        title="No diagram for this object yet"
        text="Explore how it connects to other objects instead. Imported diagrams appear here automatically."
      >
        <Button variant="contained" startIcon={<HubRoundedIcon />} onClick={() => openInExplorer()}>
          Open in Relationship Explorer
        </Button>
        {/* Nothing to draw, but a file is attached: offer it here too, not
            only from the toolbar's Files menu. */}
        {files.length > 0 && (
          <Stack spacing={0.75} sx={{ pt: 1, width: "min(360px, 100%)" }}>
            <Typography variant="caption" color="text.secondary">
              {files.length === 1 ? "1 file is attached:" : `${files.length} files are attached:`}
            </Typography>
            {files.map((file) => (
              <Button
                key={file.format}
                variant="outlined"
                size="small"
                startIcon={<DownloadRoundedIcon fontSize="small" />}
                onClick={() => downloadFile(file)}
                sx={{ justifyContent: "flex-start", textTransform: "none" }}
              >
                {file.fileName || `${formatLabel(config, file.format)} file`}
                {formatBytes(file.bytes) ? ` · ${formatBytes(file.bytes)}` : ""}
              </Button>
            ))}
          </Stack>
        )}
      </CenteredMessage>
    );
  }

  return (
    <ThemeProvider theme={businessFlowsTheme}>
      <Box
        className="bfx"
        sx={{
          flex: 1,
          height: "100%",
          minHeight: 0,
          boxSizing: "border-box",
          bgcolor: "background.default",
          p: { xs: 1, sm: 1.5, md: 2 },
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: ["minmax(0, 1fr)", showAsk ? "340px" : "", showPanel ? "360px" : ""]
            .filter(Boolean)
            .join(" "),
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minHeight: 0, minWidth: 0 }}>
          {/* Toolbar */}
          <Paper
            elevation={0}
            className="disableSelection"
            sx={{
              px: { xs: 1, md: 1.5 },
              py: 0.75,
              borderRadius: 3,
              border: `1px solid ${brand.line}`,
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: { xs: "wrap", md: "nowrap" },
            }}
          >
            <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
              <ToolButton title="Back" onClick={() => history.goBack()}>
                <ArrowBackRoundedIcon fontSize="small" />
              </ToolButton>
              <ToolButton title="Forward" onClick={() => history.goForward()}>
                <ArrowForwardRoundedIcon fontSize="small" />
              </ToolButton>
            </Stack>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />

            <Box sx={{ minWidth: 0, flex: 1, order: { xs: 3, md: 0 }, flexBasis: { xs: "100%", md: "auto" }, px: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography
                  component="h1"
                  title={info.data?.name}
                  sx={{ fontWeight: 700, fontSize: { xs: "0.95rem", md: "1.05rem" }, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {info.data?.name || (info.loading ? "Loading…" : "Diagram")}
                </Typography>
                {info.data?.type && (
                  <Chip
                    size="small"
                    label={typeLabel(config, info.data.type)}
                    sx={{ bgcolor: "rgba(240,230,49,0.35)", color: brand.ink, fontWeight: 600, flexShrink: 0 }}
                  />
                )}
                {formats.length > 1 && (
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={format}
                    onChange={(e, v) => {
                      if (!v) return;
                      userPickedFormat.current = true;
                      setFormat(v);
                    }}
                    aria-label="Diagram format"
                    sx={{
                      ml: 1,
                      flexShrink: 0,
                      alignSelf: "center",
                      // The same rounded well the tool buttons sit in, so the
                      // switch between a map and a stored file reads as part
                      // of the toolbar rather than a control dropped on it.
                      bgcolor: brand.canvas,
                      border: `1px solid ${brand.line}`,
                      borderRadius: 2.5,
                      p: 0.375,
                      gap: 0.375,
                      "& .MuiToggleButton-root": {
                        py: 0.3,
                        px: 1.1,
                        border: 0,
                        borderRadius: "8px !important",
                        fontSize: "0.74rem",
                        fontWeight: 700,
                        textTransform: "none",
                        color: brand.muted,
                        "&:hover": { bgcolor: "#fff", color: brand.ink },
                      },
                      "& .Mui-selected": { bgcolor: `${brand.ink} !important`, color: "#fff !important" },
                    }}
                  >
                    {formats.map((f) => (
                      <ToggleButton key={f} value={f} sx={{ gap: 0.5 }}>
                        {viewIcon(f)}
                        {formatLabel(config, f)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}
                {/* How the map is read. The drawn diagram is the picture; the
                    map is the same parts listed, with what each one says about
                    itself. One quiet control, in the same well as the rest. */}
                {isStructure && structure.length > 0 && (
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={mapPresentation}
                    onChange={(e, v) => {
                      if (!v) return;
                      setMapChoice(v);
                      writeMapPreference(v);
                    }}
                    aria-label="How this map is shown"
                    sx={SMALL_SWITCH}
                  >
                    {/* Said in full to anything that reads the toolbar aloud:
                        the object's own format tab is also called "Map", and
                        two buttons of that name would be one word each. */}
                    <ToggleButton
                      value={DIAGRAM}
                      aria-label="Show this map as a diagram"
                      title="Drawn from this object's own parts and relationships."
                    >
                      Diagram
                    </ToggleButton>
                    <ToggleButton
                      value={CARDS}
                      aria-label="Show this map as a list of its parts"
                      title="The same parts listed, with what each one says about itself."
                    >
                      Map
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
                {/* Which of the two this picture is. One quiet control, beside
                    the format switcher: an old export shown in the product's
                    colours and type, or the file exactly as it was imported.
                    Only geometry is never touched, so both say the same thing. */}
                {canSwitchPresentation && (
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={showingEnhanced ? ENHANCED : ORIGINAL}
                    onChange={(e, v) => {
                      if (!v) return;
                      setPresentationChoice(v);
                      writePreference(v);
                    }}
                    aria-label="How this picture is shown"
                    sx={SMALL_SWITCH}
                  >
                    <ToggleButton value={ENHANCED} title="Shown in the product's colours and type. Nothing is moved or resized.">
                      Enhanced
                    </ToggleButton>
                    <ToggleButton value={ORIGINAL} title="The file exactly as it was imported.">
                      Original
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Stack>
            </Box>

            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ ml: { xs: "auto", md: 0 }, flexShrink: 0 }}>
              <ToolGroup>
              {!zoomDisabled && (
                <>
                  <ToolButton title="Zoom out" onClick={() => canvasRef.current?.zoomOut()}>
                    <ZoomOutRoundedIcon fontSize="small" />
                  </ToolButton>
                  <ToolButton title="Zoom in" onClick={() => canvasRef.current?.zoomIn()}>
                    <ZoomInRoundedIcon fontSize="small" />
                  </ToolButton>
                  <ToolButton title="Fit the whole diagram" onClick={() => canvasRef.current?.fit()}>
                    <FitScreenRoundedIcon fontSize="small" />
                  </ToolButton>
                  {format === "bpmn" && (
                    <ToolButton title="Fill the width and read down" onClick={() => canvasRef.current?.fitWidth?.()}>
                      <WidthFullOutlinedIcon fontSize="small" />
                    </ToolButton>
                  )}
                </>
              )}
              <ToolButton title="Full screen" onClick={fullscreen}>
                <FullscreenRoundedIcon fontSize="small" />
              </ToolButton>
              </ToolGroup>

              <ToolGroup>
              <ToolButton title="Open in Relationship Explorer" onClick={() => openInExplorer()} disabled={!info.data}>
                <HubRoundedIcon fontSize="small" />
              </ToolButton>
              <ToolButton
                title={selected?.name ? `Find "${selected.name}" in Advanced search` : "Advanced search"}
                onClick={openInSearch}
              >
                <ManageSearchRoundedIcon fontSize="small" />
              </ToolButton>
              <ToolButton
                title={
                  drawingIsDownload
                    ? canDownload
                      ? "Download this drawing"
                      : DOWNLOAD_LOCKED
                    : isStructure
                    ? "Switch to a diagram format to download"
                    : !shown.data
                    ? "Nothing to download"
                    : canDownload
                    ? `Download ${formatLabel(config, format)}`
                    : DOWNLOAD_LOCKED
                }
                onClick={download}
                disabled={drawingIsDownload ? !canDownload : !shown.data || !canDownload}
              >
                {shown.data && !canDownload ? <LockRoundedIcon fontSize="small" /> : <DownloadRoundedIcon fontSize="small" />}
              </ToolButton>
              {files.length > 0 && (
                <ToolButton
                  title={`${files.length === 1 ? "1 file" : `${files.length} files`} attached, not shown in the browser`}
                  onClick={(e) => setFilesMenuAnchor(e.currentTarget)}
                >
                  <AttachFileRoundedIcon fontSize="small" />
                </ToolButton>
              )}
              </ToolGroup>

              {canAsk && (
                <Tooltip title={`${askIsOpen ? "Close" : "Open"} Ask this diagram  \u00b7  press /`}>
                  <Box
                    component="button"
                    type="button"
                    ref={askButtonRef}
                    aria-pressed={askIsOpen}
                    aria-label="Ask this diagram"
                    onClick={() => (askIsOpen ? closeAsk() : openAsk())}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      height: 34,
                      px: { xs: 0, sm: 1.25 },
                      width: { xs: 34, sm: "auto" },
                      justifyContent: "center",
                      borderRadius: 2,
                      cursor: "pointer",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      fontFamily: "inherit",
                      color: askIsOpen ? brand.ink : brand.ink,
                      bgcolor: askIsOpen ? brand.mark : "#fff",
                      border: `1px solid ${askIsOpen ? brand.signalDeep : brand.line}`,
                      "&:hover": { bgcolor: askIsOpen ? brand.mark : brand.canvas, borderColor: brand.inkSoft },
                      "&:focus-visible": { outline: `2px solid ${brand.ink}`, outlineOffset: 1 },
                    }}
                  >
                    <HighlightOutlinedIcon sx={{ fontSize: 19 }} />
                    <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                      Ask
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: { xs: "none", sm: "inline" },
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        px: 0.5,
                        borderRadius: 1,
                        bgcolor: askIsOpen ? "rgba(255,255,255,0.65)" : brand.canvas,
                        color: brand.muted,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {elements.length}
                    </Box>
                  </Box>
                </Tooltip>
              )}

              <ToolButton
                title={showPanel ? "Hide properties" : "Show properties"}
                active={showPanel}
                onClick={() => (wide ? setPanelOpen((v) => !v) : setDrawerOpen(true))}
              >
                <TuneRoundedIcon fontSize="small" />
              </ToolButton>
            </Stack>
          </Paper>

          {/* Diagram */}
          <Paper
            id="bf-diagram-stage"
            elevation={0}
            sx={{
              flex: 1,
              minHeight: { xs: 380, md: 0 },
              borderRadius: 3,
              border: `1px solid ${brand.line}`,
              overflow: "hidden",
              position: "relative",
              bgcolor: "#fff",
              backgroundImage: isTrial ? `url(${waterMark})` : "none",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundSize: "contain",
            }}
          >
            {stage}
            {/* Said once, where the reader is looking: this picture is the
                file the source system produced, and the drawing the app makes
                from this object's own parts is under Map. Plain words, and
                only where there is something better to offer - the object has
                parts - so it is never an apology for a picture that is the
                best there is. A file whose own boxes overlap says so too,
                because no amount of restyling moves them. */}
            {!isStructure && shown.data?.canEnhance && hasMap && (
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  left: 12,
                  bottom: 10,
                  maxWidth: "min(560px, calc(100% - 24px))",
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,0.92)",
                  border: `1px solid ${brand.line}`,
                  color: "text.secondary",
                }}
              >
                {shown.data?.quality === "poor"
                  ? `This picture comes from the source system and is shown as it was exported - parts of it are printed over each other there. Captions are drawn on top here so they can be read, but nothing is moved. Our own drawing of this object is under ${formatLabel(config, STRUCTURE)}.`
                  : `This picture comes from the source system and is shown as it was exported. Our own drawing of this object is under ${formatLabel(config, STRUCTURE)}.`}
              </Typography>
            )}
            {format === "bpmn" && shown.data && drillableCount > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  position: "absolute",
                  left: 12,
                  bottom: 10,
                  px: 1,
                  py: 0.25,
                  borderRadius: 2,
                  bgcolor: "rgba(255,255,255,0.92)",
                  border: `1px solid ${brand.line}`,
                  display: { xs: "none", sm: "flex" },
                  alignItems: "center",
                  gap: 0.75,
                  pointerEvents: "none",
                }}
              >
                <Box component="span" className="bf-drill-badge bf-drill-badge--legend" />
                Click a mark, double-click a step, or press Enter on one, to open its diagram
              </Typography>
            )}
          </Paper>
        </Box>

        {showAsk && (
          <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${brand.line}`, overflow: "hidden", minHeight: 0 }}>
            {askPanel}
          </Paper>
        )}

        {showPanel && (
          <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${brand.line}`, overflow: "hidden", minHeight: 0 }}>
            {detailsPanel}
          </Paper>
        )}
      </Box>

      {/* Narrow: the same panel, as a sheet over the drawing. */}
      {!wide && canAsk && (
        <Drawer
          anchor="right"
          open={askDrawerOpen}
          onClose={closeAsk}
          PaperProps={{ className: "bfx", sx: { width: "min(400px, 92vw)" } }}
        >
          {askPanel}
        </Drawer>
      )}

      {!wide && (
        <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ className: "bfx", sx: { width: "min(400px, 92vw)" } }}>
          {detailsPanel}
        </Drawer>
      )}

      <Menu
        className="bfx"
        anchorEl={filesMenuAnchor}
        open={Boolean(filesMenuAnchor)}
        onClose={() => setFilesMenuAnchor(null)}
      >
        {files.map((file) => (
          <MenuItem key={file.format} onClick={() => downloadFile(file)}>
            <ListItemIcon>
              <AttachFileRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={file.fileName || `${formatLabel(config, file.format)} file`}
              secondary={formatBytes(file.bytes) || undefined}
            />
            <DownloadRoundedIcon fontSize="small" sx={{ ml: 2, color: "text.secondary" }} />
          </MenuItem>
        ))}
      </Menu>
    </ThemeProvider>
  );
}
